import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  SaveRounded,
  DescriptionRounded,
  InfoOutlined,
  CheckCircleRounded,
  RadioButtonUncheckedRounded,
} from '@mui/icons-material';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import type {
  AcademicLevel,
  CapCertificationSummary,
  CapCode,
  CapComplianceReport,
  PriorCapRecognition,
  CapNonAcademicAttestation,
} from '@dosfilos/domain';
import { resolvePriorCapRecognition, TMAI_CAP_FRAMEWORK_2022_03 } from '@dosfilos/domain';
import { db } from '../../lib/firebase';
import { useTenantStore } from '../../stores/tenant.store';
import { capApi, type ComplianceAuditRequest } from '../../lib/api/cap.api';
import { useProgramCapTarget } from '../../hooks/use-program-cap-target';
import { CapPageHeader, CAP_CONTENT_MAX_WIDTH } from './components/CapPageHeader';
import { errorText } from '../../lib/error-text';
import { formatDate, formatDateTime } from '../../lib/i18n-format';

// ─── CAP compliance dashboard (Phase E — CAP, PR6) — wireframe screen 1 ────
// Runs the audit read-only (preview) and lets the registrar save an immutable
// snapshot. overallStatus is hard-gated; advisory design signals show as
// warnings.

type Report = Omit<CapComplianceReport, 'id'>;

const NON_ACADEMIC_KEYS = [
  'instructional_language',
  'faculty_credentials',
  'entrance_requirements',
  'spiritual_health',
] as const;

const AREA_LABEL: Record<string, string> = {
  OT_SURVEY: 'OT_SURVEY',
  NT_SURVEY: 'NT_SURVEY',
  HERMENEUTICS: 'HERMENEUTICS',
  THEOLOGY: 'THEOLOGY',
  TEXTUAL_EXEGESIS: 'TEXTUAL_EXEGESIS',
  EXPOSITORY_PREACHING: 'EXPOSITORY_PREACHING',
  PASTORAL_MINISTRIES: 'PASTORAL_MINISTRIES',
  ELECTIVES: 'ELECTIVES',
};

export function ComplianceDashboardPage(): JSX.Element {
  const { t } = useTranslation('cap');
  const navigate = useNavigate();
  const { programId } = useParams<{ programId: string }>();
  const { currentTenantId } = useTenantStore();

  const [capCode, setCapCode] = useState<CapCode>('BSC');
  const [level, setLevel] = useState<AcademicLevel>('undergraduate');
  /** Quién reconoce el CAP previo: TMAI, con una certificación otorgada al
   *  peldaño anterior. Era una casilla que marcaba el propio centro, y marcarla
   *  BAJA los mínimos — el evaluado se rebajaba el estándar. */
  const [priorCap, setPriorCap] = useState<
    (PriorCapRecognition & { priorProgramName?: string }) | null
  >(null);

  // Default CAP + level to the program's target (still switchable).
  const capTarget = useProgramCapTarget(programId);
  useEffect(() => {
    if (!capTarget) return;
    setCapCode(capTarget.capCode);
    setLevel(capTarget.level);
  }, [capTarget]);

  // El mismo cálculo que hace el servidor al auditar, con los mismos datos: el
  // espejo de certificaciones del centro y el peldaño que declara el plan.
  useEffect(() => {
    let alive = true;
    void (async () => {
      if (!currentTenantId || !programId) return;
      try {
        const [programSnap, certSnap] = await Promise.all([
          getDoc(doc(db, `tenants/${currentTenantId}/programs/${programId}`)),
          getDocs(collection(db, `tenants/${currentTenantId}/capCertifications`)),
        ]);
        const stacksOnProgramId = programSnap.data()?.['stacksOnProgramId'] as string | undefined;
        const resolved = resolvePriorCapRecognition({
          framework: TMAI_CAP_FRAMEWORK_2022_03,
          capCode,
          level,
          stacksOnProgramId,
          certifications: certSnap.docs.map((d) => d.data() as CapCertificationSummary),
        });
        let priorProgramName: string | undefined;
        if (resolved.priorProgramId) {
          const prior = await getDoc(
            doc(db, `tenants/${currentTenantId}/programs/${resolved.priorProgramId}`),
          );
          priorProgramName = prior.data()?.['name'] as string | undefined;
        }
        if (alive) setPriorCap({ ...resolved, ...(priorProgramName ? { priorProgramName } : {}) });
      } catch {
        if (alive) setPriorCap(null);
      }
    })();
    return () => {
      alive = false;
    };
  }, [currentTenantId, programId, capCode, level]);
  // LO QUE EL CENTRO DECLARÓ, leído del servidor. Antes esto era estado local
  // puro: al recargar volvía a cero y la auditoría reportaba los cuatro
  // requisitos sin atestar aunque se hubieran marcado ayer.
  const [nonAcademic, setNonAcademic] = useState<Record<string, boolean>>({});
  const [attesting, setAttesting] = useState<string | null>(null);
  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);
  const [snapshots, setSnapshots] = useState<CapComplianceReport[]>([]);

  const request = useCallback(
    (): ComplianceAuditRequest => ({
      tenantId: currentTenantId ?? '',
      programId: programId ?? '',
      capCode,
      level,
    }),
    // `nonAcademic` NO va acá: ya no viaja en la petición, y dejarlo recreaba
    // `request` en cada casilla, lo que volvía a correr la auditoría por el
    // efecto ADEMÁS de la corrida que dispara `attest`.
    [currentTenantId, programId, capCode, level],
  );

  // Staleness guard: capCode/level flip (BSC default → program's capTarget) fire
  // two audits; only the latest response may apply so the older doesn't overwrite it.
  const runReq = useRef(0);
  const run = useCallback(async (): Promise<void> => {
    if (!currentTenantId || !programId) return;
    const req = ++runReq.current;
    setLoading(true);
    setError(null);
    try {
      const r = await capApi.runComplianceAudit(request());
      if (runReq.current === req) setReport(r);
    } catch (e) {
      if (runReq.current === req) setError(errorText(e));
    } finally {
      if (runReq.current === req) setLoading(false);
    }
  }, [currentTenantId, programId, request]);

  // Se cargan por programa Y CAP: el mismo programa puede perseguir el BSC y el
  // DEP, y lo declarado para uno no vale para el otro.
  useEffect(() => {
    if (!currentTenantId || !programId) return;
    let alive = true;
    void capApi
      .getNonAcademicAttestations({ tenantId: currentTenantId, programId, capCode, level })
      .then((r) => {
        if (!alive) return;
        setNonAcademic(Object.fromEntries(r.attestations.map((a) => [a.key, a.met])));
      })
      .catch(() => {
        /* no bloquea: la auditoría igual lee la verdad del servidor */
      });
    return () => {
      alive = false;
    };
  }, [currentTenantId, programId, capCode, level]);

  /** Declarar una. Se guarda ANTES de reflejarla: si el guardado falla, la
   *  casilla no debe quedar marcada mostrando algo que el servidor no sabe. */
  const attest = async (key: string, met: boolean): Promise<void> => {
    if (!currentTenantId || !programId) return;
    setAttesting(key);
    try {
      await capApi.setNonAcademicAttestation({
        tenantId: currentTenantId,
        programId,
        capCode,
        level,
        key: key as CapNonAcademicAttestation['key'],
        met,
      });
      setNonAcademic((n) => ({ ...n, [key]: met }));
      // La auditoría la lee del servidor, así que hay que volver a correrla para
      // que lo que se ve corresponda con lo que se acaba de declarar.
      void run();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setAttesting(null);
    }
  };

  const snapReq = useRef(0);
  const loadSnapshots = useCallback(async (): Promise<void> => {
    if (!currentTenantId || !programId) return;
    const req = ++snapReq.current;
    try {
      const res = await capApi.listComplianceSnapshots({
        tenantId: currentTenantId,
        programId,
        capCode,
      });
      if (snapReq.current === req) setSnapshots(res.snapshots);
    } catch {
      /* non-blocking */
    }
  }, [currentTenantId, programId, capCode]);

  // Re-run when the CAP/level/attestations change so the preview stays live.
  useEffect(() => {
    void run();
  }, [run]);
  useEffect(() => {
    void loadSnapshots();
  }, [loadSnapshots]);

  const save = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      await capApi.saveComplianceSnapshot(request());
      setSavedMsg(t('compliance.snapshotSaved'));
      await loadSnapshots();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setSaving(false);
    }
  };

  const pct =
    report && report.totalMinTcu > 0
      ? Math.min(100, Math.round((report.totalActualTcu / report.totalMinTcu) * 100))
      : 0;
  const evaluatedCount = report?.ploCoverage.filter((p) => p.evaluated).length ?? 0;

  return (
    <Box>
      <CapPageHeader
        backLabel={t('compliance.back')}
        onBack={() => navigate(-1)}
        title={t('compliance.title')}
        subtitle={t('compliance.subtitle')}
      />
      <Box sx={{ px: 3, py: 3, pb: 6, maxWidth: CAP_CONTENT_MAX_WIDTH, mx: 'auto' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={2} flexWrap="wrap">
          <TextField
            select
            size="small"
            label={t('compliance.cap')}
            value={capCode}
            onChange={(e) => setCapCode(e.target.value as CapCode)}
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="BSC">{t('plos.caps.BSC')}</MenuItem>
            <MenuItem value="DEP">{t('plos.caps.DEP')}</MenuItem>
          </TextField>
          <TextField
            select
            size="small"
            label={t('compliance.level')}
            value={level}
            onChange={(e) => setLevel(e.target.value as AcademicLevel)}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="undergraduate">{t('levels.undergraduate')}</MenuItem>
            <MenuItem value="graduate">{t('levels.graduate')}</MenuItem>
          </TextField>
          {/* Informa, no pregunta: lo reconoce una certificación de TMAI. */}
          {priorCap && priorCap.reason !== 'cap_has_no_prerequisite' && (
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <Chip
                size="small"
                color={priorCap.recognized ? 'success' : 'default'}
                variant={priorCap.recognized ? 'filled' : 'outlined'}
                label={
                  priorCap.recognized
                    ? t('compliance.priorCapGranted', {
                        cap: priorCap.prerequisiteCapCode,
                        date: formatDate(priorCap.grantedAt),
                      })
                    : priorCap.reason === 'no_prior_program'
                      ? t('compliance.priorCapNoLadder')
                      : t('compliance.priorCapPending', {
                          program: priorCap.priorProgramName ?? priorCap.priorProgramId,
                        })
                }
              />
              <Tooltip title={t('compliance.stackingHelp')} arrow>
                <InfoOutlined sx={{ fontSize: 16, color: 'text.disabled' }} />
              </Tooltip>
            </Stack>
          )}
        </Stack>

        {/* Subtle "refreshing" bar on re-audit — keeps content instead of blanking. */}
        <Box sx={{ height: 4, mb: 1.5 }}>
          {loading && report && <LinearProgress sx={{ borderRadius: 4 }} />}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading && !report ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : report ? (
          <>
            {/* KPI cards */}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mb={2} alignItems="stretch">
              {/* Overall status */}
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  flex: 1,
                  borderRadius: 3,
                  borderLeft: '4px solid',
                  borderLeftColor:
                    report.overallStatus === 'compliant' ? 'success.main' : 'warning.main',
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
                    {t('compliance.overallStatus')}
                  </Typography>
                  <Chip
                    size="small"
                    sx={{ fontWeight: 700 }}
                    color={report.overallStatus === 'compliant' ? 'success' : 'warning'}
                    label={t(`compliance.status.${report.overallStatus}`)}
                  />
                </Stack>
                <Stack direction="row" alignItems="baseline" spacing={0.75} mt={0.5}>
                  <Typography variant="h3" fontWeight={800} lineHeight={1}>
                    {report.totalActualTcu}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    / {report.totalMinTcu} TCU
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={pct}
                  color={pct >= 100 ? 'success' : 'warning'}
                  sx={{ mt: 1, height: 8, borderRadius: 4 }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.5, display: 'block' }}
                >
                  {t('compliance.tcuCovered', { pct })}
                </Typography>
              </Paper>

              {/* PLO coverage */}
              {(() => {
                const allEvaluated =
                  report.ploCoverage.length > 0 && evaluatedCount === report.ploCoverage.length;
                return (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      flex: 1,
                      borderRadius: 3,
                      borderLeft: '4px solid',
                      borderLeftColor: allEvaluated ? 'success.main' : 'warning.main',
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Typography
                        variant="overline"
                        color="text.secondary"
                        sx={{ fontWeight: 700 }}
                      >
                        {t('compliance.ploCoverage')}
                      </Typography>
                      <Chip
                        size="small"
                        sx={{ fontWeight: 700 }}
                        color={allEvaluated ? 'success' : 'default'}
                        label={`${evaluatedCount}/${report.ploCoverage.length}`}
                      />
                    </Stack>
                    <Stack direction="row" alignItems="baseline" spacing={0.75} mt={0.5}>
                      <Typography variant="h3" fontWeight={800} lineHeight={1}>
                        {evaluatedCount}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        / {report.ploCoverage.length} {t('compliance.evaluated')}
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={
                        report.ploCoverage.length > 0
                          ? (evaluatedCount / report.ploCoverage.length) * 100
                          : 0
                      }
                      color={allEvaluated ? 'success' : 'warning'}
                      sx={{ mt: 1, height: 8, borderRadius: 4 }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ mt: 0.5, display: 'block', fontWeight: 600 }}
                      color={allEvaluated ? 'success.main' : 'warning.main'}
                    >
                      {allEvaluated
                        ? t('compliance.plosAllEvaluated')
                        : t('compliance.plosWithoutE', {
                            count: report.ploCoverage.filter((p) => !p.evaluated).length,
                          })}
                    </Typography>
                  </Paper>
                );
              })()}
            </Stack>

            {/* Subject-area table (≈70%) + non-academic attestations (≈30%) */}
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              mb={2}
              alignItems="flex-start"
            >
              <Paper
                variant="outlined"
                sx={{ p: 2.5, borderRadius: 3, flex: { md: 7 }, width: '100%', minWidth: 0 }}
              >
                <Typography variant="subtitle1" fontWeight={700} mb={1.5}>
                  {t('compliance.bySubjectArea')}
                </Typography>
                <TableContainer sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('compliance.area')}</TableCell>
                        <TableCell align="right">{t('compliance.minTcu')}</TableCell>
                        <TableCell align="right">{t('compliance.actualTcu')}</TableCell>
                        <TableCell sx={{ width: 160 }}>{t('compliance.coverage')}</TableCell>
                        <TableCell>{t('compliance.state')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {report.subjectAreas.map((a) => {
                        const cov =
                          a.minTcu > 0 ? Math.min(100, (a.actualTcu / a.minTcu) * 100) : 100;
                        return (
                          <TableRow key={a.code} hover>
                            <TableCell>{t(`subjectAreas.${AREA_LABEL[a.code]}`)}</TableCell>
                            <TableCell align="right">{a.minTcu}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                              {a.actualTcu}
                            </TableCell>
                            <TableCell>
                              <LinearProgress
                                variant="determinate"
                                value={cov}
                                color={a.met ? 'success' : 'error'}
                                sx={{ height: 6, borderRadius: 3, minWidth: 80 }}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                color={a.met ? 'success' : 'error'}
                                label={
                                  a.met
                                    ? t('compliance.met')
                                    : t('compliance.missing', { tcu: a.gapTcu })
                                }
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, borderBottom: 'none' }}>
                          {t('compliance.areaTotal')}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, borderBottom: 'none' }}>
                          {report.totalMinTcu}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, borderBottom: 'none' }}>
                          {report.totalActualTcu}
                        </TableCell>
                        <TableCell sx={{ borderBottom: 'none' }} />
                        <TableCell sx={{ borderBottom: 'none' }}>
                          <Chip
                            size="small"
                            color={report.overallStatus === 'compliant' ? 'success' : 'warning'}
                            label={t(`compliance.status.${report.overallStatus}`)}
                          />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>

              {/* Non-academic attestations (side, ≈30%) */}
              {(() => {
                const doneCount = NON_ACADEMIC_KEYS.filter((k) => nonAcademic[k]).length;
                return (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2.5,
                      borderRadius: 3,
                      flex: { md: 3 },
                      width: '100%',
                      borderLeft: '4px solid',
                      borderLeftColor:
                        doneCount === NON_ACADEMIC_KEYS.length ? 'success.main' : 'divider',
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={1}
                    >
                      <Typography
                        variant="overline"
                        color="text.secondary"
                        sx={{ fontWeight: 700 }}
                      >
                        {t('compliance.nonAcademic')}
                      </Typography>
                      <Chip
                        size="small"
                        color={doneCount === NON_ACADEMIC_KEYS.length ? 'success' : 'default'}
                        label={t('compliance.nonAcademicCount', {
                          done: doneCount,
                          total: NON_ACADEMIC_KEYS.length,
                        })}
                      />
                    </Stack>
                    <Stack spacing={0.25}>
                      {NON_ACADEMIC_KEYS.map((key) => (
                        <FormControlLabel
                          key={key}
                          sx={{ mr: 0 }}
                          control={
                            <Checkbox
                              size="small"
                              icon={<RadioButtonUncheckedRounded fontSize="small" />}
                              checkedIcon={<CheckCircleRounded fontSize="small" />}
                              checked={nonAcademic[key] ?? false}
                              disabled={attesting === key}
                              onChange={(e) => void attest(key, e.target.checked)}
                            />
                          }
                          label={
                            <Typography variant="body2">
                              {t(`compliance.nonAcademicKeys.${key}`)}
                            </Typography>
                          }
                        />
                      ))}
                    </Stack>
                  </Paper>
                );
              })()}
            </Stack>

            {/* Gaps + warnings */}
            {report.coursesWithoutCcd.length > 0 && (
              <Alert severity="error" sx={{ mb: 1 }}>
                {t('compliance.coursesWithoutCcd', {
                  courses: report.coursesWithoutCcd.map((c) => c.courseName).join(', '),
                })}
              </Alert>
            )}
            {report.warnings.coursesWithoutContribution.length > 0 && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                {t('compliance.warnCoursesWithoutContribution', {
                  count: report.warnings.coursesWithoutContribution.length,
                })}
              </Alert>
            )}
            {report.warnings.plosWithoutFullCoverage.length > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {t('compliance.warnPlosWithoutFullCoverage', {
                  count: report.warnings.plosWithoutFullCoverage.length,
                })}
              </Alert>
            )}
            {report.warnings.stackingRequestedButNotSealed && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                {t('compliance.warnStackingUnsealed')}
              </Alert>
            )}
            {report.warnings.coursesOutsideCapAreas.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {t('compliance.warnCoursesOutsideCapAreas', {
                  courses: report.warnings.coursesOutsideCapAreas
                    .map((c) => `${c.courseName} (${c.area})`)
                    .join(', '),
                })}
              </Alert>
            )}

            <Stack direction="row" spacing={1.5} flexWrap="wrap">
              <Button
                variant="outlined"
                startIcon={saving ? <CircularProgress size={16} /> : <SaveRounded />}
                disabled={saving}
                onClick={() => void save()}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                {t('compliance.saveSnapshot')}
              </Button>
              <Button
                variant="contained"
                disableElevation
                startIcon={<DescriptionRounded />}
                onClick={() => navigate(`/cap/${programId}/propuesta`)}
                sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                {t('compliance.generateProposal')}
              </Button>
            </Stack>

            {snapshots.length > 0 && (
              <>
                <Divider sx={{ my: 3 }} />
                <Typography variant="subtitle1" fontWeight={700} mb={1.5}>
                  {t('compliance.previousSnapshots')}
                </Typography>
                <Stack spacing={1}>
                  {snapshots.map((s) => (
                    <Paper key={s.id} variant="outlined" sx={{ px: 2, py: 1.25, borderRadius: 2 }}>
                      <Stack
                        direction="row"
                        spacing={1.5}
                        alignItems="center"
                        justifyContent="space-between"
                        flexWrap="wrap"
                      >
                        <Chip
                          size="small"
                          color={s.overallStatus === 'compliant' ? 'success' : 'warning'}
                          label={t(`compliance.status.${s.overallStatus}`)}
                        />
                        <Typography variant="body2" color="text.secondary">
                          {formatDateTime(s.generatedAt)}
                        </Typography>
                        <Typography variant="body2" fontWeight={700}>
                          {s.totalActualTcu} / {s.totalMinTcu} TCU
                        </Typography>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              </>
            )}
          </>
        ) : null}

        <Snackbar
          open={savedMsg !== null}
          autoHideDuration={3000}
          onClose={() => setSavedMsg(null)}
          message={savedMsg ?? ''}
        />
      </Box>
    </Box>
  );
}
